import React, { useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { CellValueChangedEvent, ColDef } from 'ag-grid-community';

import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import { useClientSidePagination } from '@/components/common/Pagination/useClientSidePagination';
import Pagination from '@/components/common/Pagination/Pagination';
import { CONTRACT_LINES_PER_PAGE } from '@/config/common.config';
import AgGridDropdownEditor from '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridDropdownEditor';
import AgGridDatepickerTag4Editor from
  '@/components/common/TableGrid/CustomComponents/AgGridDatepicker/AgGridDatepickerTag4Editor';
import AgGridCellRenderer from '@/components/common/TableGrid/CustomComponents/AgGridCellRenderer/AgGridCellRenderer';
import { ContractLineBillingCycleOutput } from '@/graphql/contracts/_generated_/contracts';

interface IModalContractLinesProps {
  contractLines: ContractLineBillingCycleOutput[];
  columns: ColDef[];
  contract: Contract;
  onCellValueChanged: () => void;
}

export default function ModalContractLines({
  contractLines,
  columns,
  contract,
  onCellValueChanged
}: IModalContractLinesProps) {
  const { currentPage, totalPages, onPaginationChanged } =
    useClientSidePagination(contractLines, CONTRACT_LINES_PER_PAGE);
  const gridRef = useRef<AgGridReact>(null);

  const onClickPage = (page: number) => {
    gridRef.current?.api?.paginationGoToPage(page - 1);
  };

  const onChangeCell = ({ api, node }:
  CellValueChangedEvent<ContractLine | ContractLineBillingCycleOutput, any>) => {
    api.redrawRows({
      rowNodes: [node]
    });

    onCellValueChanged();
  };

  return (
    <>
      <TableGrid<ContractLine | ContractLineBillingCycleOutput>
        rowData={contractLines?.length ? contractLines : []}
        columnDefs={columns}
        onPaginationChanged={onPaginationChanged}
        pagination={true}
        paginationPageSize={CONTRACT_LINES_PER_PAGE}
        suppressPaginationPanel={true}
        ref={gridRef}
        components={{
          DropdownEditor: AgGridDropdownEditor,
          DatepickerEditor: AgGridDatepickerTag4Editor,
          CellRenderer: AgGridCellRenderer,
        }}
        context={{ contract }}
        onCellValueChanged={onChangeCell}
        getRowId={(el) =>
          (el.data as unknown as ContractLine)?.contractLineID ??
          (el.data as unknown as ContractLineBillingCycleOutput)?.id
        }
        defaultColDef={{
          suppressMovable: true,
          sortable: false,
          resizable: false
        }}
      />

      {totalPages > 1 && (
        <div className="d-flex justify-content-end pt-10 pb-10 pr-20">
          <Pagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            onClickPage={onClickPage}
          />
        </div>
      )}
    </>
  );
}
